import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../aws/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export const useClientes = () => {
  const { getJwtToken } = useAuth();
  
  return useQuery({
    queryKey: ['clientes'],
    queryFn: async () => {
      const token = await getJwtToken();
      const { data } = await axios.get(`${API_BASE_URL}/v1-clientes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data;
    },
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
};

export const useCrearCliente = () => {
  const queryClient = useQueryClient();
  const { getJwtToken } = useAuth();

  return useMutation({
    mutationFn: async (payload) => {
      const token = await getJwtToken();
      const { data } = await axios.post(`${API_BASE_URL}/v1-clientes`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['clientes']);
    }
  });
};

export const useGetNotasCRM = (clienteId) => {
  const { getJwtToken } = useAuth();
  
  return useQuery({
    queryKey: ['crm_notas', clienteId],
    queryFn: async () => {
      if (!clienteId) return [];
      const token = await getJwtToken();
      const { data } = await axios.get(`${API_BASE_URL}/v1-crm-notas?cliente_id=${clienteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data;
    },
    enabled: !!clienteId // Evita peticiones innecesarias al servidor
  });
};

export const useSaveNotaCRM = () => {
  const queryClient = useQueryClient();
  const { getJwtToken } = useAuth();

  return useMutation({
    mutationFn: async (payload) => {
      const token = await getJwtToken();
      const { data } = await axios.post(`${API_BASE_URL}/v1-crm-notas`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['crm_notas', variables.cliente_id]);
    }
  });
};

export const useUpdateNotaCRM = () => {
  const queryClient = useQueryClient();
  const { getJwtToken } = useAuth();

  return useMutation({
    mutationFn: async (payload) => {
      const token = await getJwtToken();
      const { id, ...dataToUpdate } = payload;
      
      const { data } = await axios.put(`${API_BASE_URL}/v1-crm-notas/${id}`, dataToUpdate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['crm_notas', variables.cliente_id]);
    }
  });
};

export const useDeleteNotaCRM = () => {
  const queryClient = useQueryClient();
  const { getJwtToken } = useAuth();

  return useMutation({
    mutationFn: async (id) => {
      const token = await getJwtToken();
      const { data } = await axios.delete(`${API_BASE_URL}/v1-crm-notas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data;
    },
    onSuccess: () => {
      // Como aquí solo pasamos el ID de la nota y no el del cliente, 
      // le decimos a React Query que limpie TODA la caché de notas para forzar un refresco.
      queryClient.invalidateQueries(['crm_notas']);
    }
  });
};